import { ApiDataAccess, ApiResourceLocation, Dictionary } from '@softline/core';
import { GearType } from '../models/gear-type';
import { Vehicle } from '../models/vehicle';
import { mapVehiclePage, VehiclePage } from '../models/vehicle-page';

export abstract class GearRepository<T> {
  abstract retrieve(
    query?: string,
    limit?: number,
    accountId?: number,
    offset?: number
  ): Promise<VehiclePage>;
  abstract retrieveById(id: number): Promise<T>;
  abstract updateById(id: number, body: Partial<T>): Promise<T>;
  abstract create(vehicle: Partial<T>): Promise<T>;
}

export class DefaultGearRepository<T> extends GearRepository<T> {
  constructor(
    protected dataAccess: ApiDataAccess,
    protected type: GearType,
    protected paths: {
      retrieve: string;
      retrieveById: string;
      updateById: string;
      create: string;
    }
  ) {
    super();
  }

  private createLocation(
    path: string,
    pathParams?: Dictionary<unknown>
  ): ApiResourceLocation {
    return pathParams ? { path, pathParams } : { path };
  }

  async retrieve(
    query?: string | null,
    limit?: number,
    accountId?: number,
    offset?: number
  ): Promise<VehiclePage> {
    const response = await this.dataAccess.get<VehiclePage | Vehicle[]>({
      path: this.paths.retrieve,
      queryParams: { type: this.type, accountId, limit, offset, query },
    });
    return mapVehiclePage(response);
  }

  retrieveById(id: number): Promise<T> {
    return this.dataAccess.get(this.createLocation(this.paths.retrieveById, { id }));
  }

  updateById(id: number, body: Partial<T>): Promise<T> {
    return this.dataAccess.update(
      this.createLocation(this.paths.updateById, { id }),
      body
    );
  }

  create(vehicle: Partial<T>): Promise<T> {
    return this.dataAccess.create(this.createLocation(this.paths.create), vehicle);
  }
}
